import { z } from "zod";
import type { UserRef } from "./api-types";

/**
 * Invoices and payments (spec §9). Amounts in cents.
 * - Card surcharge: passed to the customer on credit cards only (never debit or prepaid), at most
 *   3%, shown before they pay. Card payments only up to a limit (default $2,000); above it, ACH.
 * - ACH: Stripe's fee (0.8%, capped at $5) is absorbed; ACH is the free option.
 */

/** Card-network cap on a surcharge: 3%. */
export const MAX_SURCHARGE_BPS = 300;
export const ACH_FEE_BPS = 80;
export const ACH_FEE_CAP_CENTS = 500;

/** x × bps / 10,000, rounded half up (whole-number math). */
function bpsOf(amountCents: number, bps: number): number {
  const p = amountCents * bps;
  return Math.floor(p / 10_000) + (p % 10_000 >= 5_000 ? 1 : 0);
}

/** The surcharge on a card payment. Only credit cards are surcharged; unknown funding isn't. */
export function cardSurchargeCents(amountCents: number, bps: number, funding: "credit" | "debit" | "prepaid" | "unknown"): number {
  if (!Number.isInteger(amountCents) || amountCents <= 0) return 0;
  if (funding !== "credit") return 0;
  return bpsOf(amountCents, Math.min(Math.max(0, Math.floor(bps)), MAX_SURCHARGE_BPS));
}

/** What Stripe charges NMT for an ACH payment (absorbed, not charged to the customer). */
export function achFeeCents(amountCents: number): number {
  return Math.min(bpsOf(amountCents, ACH_FEE_BPS), ACH_FEE_CAP_CENTS);
}

/** Card payments are only for amounts up to the limit; bigger ones go by ACH. */
export function cardAllowed(amountCents: number, limitCents: number): boolean {
  return amountCents > 0 && amountCents <= limitCents;
}

export type InvoiceStatus = "draft" | "sent" | "partial" | "paid" | "void";

/**
 * Paid so far (succeeded payments, excluding surcharges) and credited (credit memos and write-offs
 * applied to it, which aren't money received) → balance and status.
 */
export function invoiceState(totalCents: number, paidCents: number, sent: boolean, voided: boolean, creditedCents = 0): { balanceCents: number; status: InvoiceStatus } {
  const settled = paidCents + creditedCents;
  const balanceCents = Math.max(0, totalCents - settled);
  if (voided) return { balanceCents, status: "void" };
  // Paid in full; also a $0 invoice that has payments (an empty copy from the old QuickBooks → JobNimbus sync).
  if (settled > 0 && settled >= totalCents) return { balanceCents: 0, status: "paid" };
  if (settled > 0) return { balanceCents, status: "partial" };
  return { balanceCents, status: sent ? "sent" : "draft" };
}

/**
 * Credit memos and write-offs settle part of an invoice without money coming in. They're kept with
 * payments (so a balance is total − everything applied) but never counted as collected.
 */
export const ADJUSTMENT_METHODS = ["credit", "writeoff"] as const;
export const isAdjustment = (method: string) => (ADJUSTMENT_METHODS as readonly string[]).includes(method);

/** An invoice's succeeded payments split into money received and credits/write-offs. */
export function settledBy(ps: readonly { method: string; amountCents: number; status: string }[]): { paidCents: number; creditedCents: number } {
  let paidCents = 0;
  let creditedCents = 0;
  for (const p of ps) {
    if (p.status !== "succeeded") continue;
    if (isAdjustment(p.method)) creditedCents += p.amountCents;
    else paidCents += p.amountCents;
  }
  return { paidCents, creditedCents };
}

/**
 * A job's money. The contract is the signed proposal plus change orders, or what's been invoiced if
 * more; paid is money received; credits are credit memos (applied or not) and write-offs, which
 * lower what's owed. Balance = contract − paid − credits, never below zero.
 */
export function jobMoneyOf(x: { contractCents: number; invoicedCents: number; paidCents: number; creditCents: number }) {
  const contractCents = Math.max(x.contractCents, x.invoicedCents);
  return { contractCents, paidCents: x.paidCents, creditCents: x.creditCents, balanceCents: Math.max(0, contractCents - x.paidCents - x.creditCents) };
}

// ── API shapes ───────────────────────────────────────────────────────────

export const PAYMENT_METHOD_KINDS = ["card", "ach", "check", "cash", "financing", "other", "credit", "writeoff"] as const;
export type PaymentMethodKind = (typeof PAYMENT_METHOD_KINDS)[number];
export const PAYMENT_METHOD_LABELS: Record<PaymentMethodKind, string> = { card: "Card", ach: "Bank (ACH)", check: "Check", cash: "Cash", financing: "Financing", other: "Other", credit: "Credit memo", writeoff: "Write-off" };

/** Where an invoice or payment came from. Ones from JobNimbus or QuickBooks are read-only here. */
export type MoneySource = "crm" | "jobnimbus" | "quickbooks";
export const MONEY_SOURCE_LABELS: Record<MoneySource, string> = { crm: "CRM", jobnimbus: "JobNimbus", quickbooks: "QuickBooks" };

export interface InvoiceLine {
  description: string;
  /** Negative only for retainage held on a pay app. */
  amountCents: number;
  /** Retainage held or released (QuickBooks: the retainage item). */
  retainage?: boolean;
  /** Optional quantity and rate; when given, amount = quantity × rate (rounded to the cent). */
  quantity?: number;
  unitPriceCents?: number;
}

/** quantity × rate in cents, rounded half up (quantity to 4 decimal places; exact integer math). */
export function lineAmountCents(quantity: number, unitPriceCents: number): number {
  const q = BigInt(Math.round(quantity * 10_000));
  const p = q * BigInt(unitPriceCents);
  const neg = p < 0n;
  const a = neg ? -p : p;
  const whole = a / 10_000n + (a % 10_000n >= 5_000n ? 1n : 0n);
  return Number(neg ? -whole : whole);
}

export interface PaymentItem {
  id: string;
  invoiceId: string;
  method: PaymentMethodKind;
  amountCents: number;
  surchargeCents: number;
  feeCents: number | null;
  status: "pending" | "succeeded" | "failed" | "refunded" | "void";
  reference: string;
  note: string;
  receivedAt: string;
  recordedBy: UserRef | null;
  error: string | null;
  source: MoneySource;
}

export interface InvoiceItem {
  /** The signed custom proposal and payment it bills ("m:<milestone id>"), if any. */
  proposalId: string | null;
  proposalPart: string | null;
  id: string;
  number: number;
  jobId: string;
  jobLabel: string;
  title: string;
  lines: InvoiceLine[];
  totalCents: number;
  /** Money received. */
  paidCents: number;
  /** Credit memos and write-offs applied. */
  creditedCents: number;
  balanceCents: number;
  status: InvoiceStatus;
  source: MoneySource;
  /** Its number in JobNimbus or QuickBooks (NMT-2025-2279-1), when it came from there. */
  externalNumber: string;
  invoiceDate: string | null;
  /** The QuickBooks invoice it's linked to, how it was matched, and what QuickBooks says. */
  quickbooks: { id: string; matchedBy: "linked" | "number" | "amount" | "created"; totalCents: number | null; balanceCents: number | null; checkedAt: string | null } | null;
  /** When it was last looked for in QuickBooks (null: QuickBooks hasn't been checked). */
  qbCheckedAt: string | null;
  /** Where the CRM and QuickBooks disagree (total or balance), in words. */
  mismatch: string | null;
  /** Why it's void, when it wasn't voided by hand ("Deleted in QuickBooks"). */
  voidReason: string | null;
  /** Card allowed over the company's card limit on this invoice. */
  cardOverLimit: boolean;
  dueDate: string | null;
  memo: string;
  sentAt: string | null;
  createdAt: string;
  createdBy: UserRef | null;
  payments: PaymentItem[];
  /** Only right after sending or making a new link. */
  payUrl?: string;
}

/** A past-due invoice on the dashboard card. */
export interface PastDueItem {
  invoiceId: string;
  /** Its number here, or JobNimbus's / QuickBooks's. */
  number: string;
  source: MoneySource;
  /** Can be sent and paid from here (a CRM invoice, or one brought in that is linked to QuickBooks). */
  collectable: boolean;
  title: string;
  jobId: string;
  jobNumber: number;
  jobTitle: string;
  customerName: string;
  balanceCents: number;
  dueDate: string;
  daysLate: number;
  /** A payment is processing (ACH): not really late. */
  pending: boolean;
  remindedAt: string | null;
  remindedVia: "email" | "text" | null;
  /** The commissioned rep and the other reps on the job. */
  repIds: string[];
  repName: string | null;
}

/** A QuickBooks credit memo on a job: what isn't applied yet is still owed back to the customer. */
export interface CreditMemoItem {
  id: string;
  number: string;
  date: string | null;
  totalCents: number;
  unappliedCents: number;
  lines: InvoiceLine[];
  memo: string;
}

/** An estimate brought in from JobNimbus (read-only). */
export interface JobEstimateItem {
  id: string;
  source: "jobnimbus";
  number: string;
  title: string;
  date: string | null;
  status: string;
  totalCents: number;
  lines: InvoiceLine[];
}

/** Suggested invoices from the job's signed proposal (deductible, ACV, depreciation...). */
export interface InvoiceSuggestion {
  label: string;
  amountCents: number;
  when: string;
  invoiced: boolean;
  /** Custom proposals: the payment in its schedule ("m:<milestone id>"), and the invoice made for it. */
  proposalId?: string;
  part?: string;
  proposalTitle?: string;
  /** As a percent of the contract (thousandths of a percent); null for a set amount or the rest. */
  percentMilli?: number | null;
  invoice?: { id: string; number: number; status: InvoiceStatus } | null;
}

/** A line's description: as long as QuickBooks takes on a line (4,000 characters). */
export const LINE_MAX = 4000;
const line = z
  .object({
    description: z.string().trim().min(1, "Describe the line").max(LINE_MAX),
    amountCents: z.number().int().min(0).max(100_000_000),
    quantity: z.number().positive().max(1_000_000).optional(),
    unitPriceCents: z.number().int().min(0).max(100_000_000).optional(),
  })
  .refine((l) => (l.quantity === undefined) === (l.unitPriceCents === undefined), { message: "Give both the quantity and the rate, or neither" })
  .refine((l) => l.quantity === undefined || l.unitPriceCents === undefined || l.amountCents === lineAmountCents(l.quantity, l.unitPriceCents), { message: "The amount has to be quantity × rate" });

export const invoiceInputSchema = z.object({
  title: z.string().trim().max(120).default(""),
  lines: z.array(line).min(1, "Add a line").max(50),
  dueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().default(null),
  memo: z.string().trim().max(2000).default(""),
  /** Billing a payment from a signed custom proposal's schedule. */
  proposalId: z.string().uuid().optional(),
  proposalPart: z.string().regex(/^m:[A-Za-z0-9_-]{1,40}$/).optional(),
});

export const invoiceUpdateSchema = z.object({
  title: z.string().trim().max(120).optional(),
  lines: z.array(line).min(1).max(50).optional(),
  dueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  memo: z.string().trim().max(2000).optional(),
});

export const manualPaymentSchema = z.object({
  method: z.enum(["check", "cash", "financing", "other"]),
  amountCents: z.number().int().min(1).max(100_000_000),
  reference: z.string().trim().max(100).default(""),
  note: z.string().trim().max(1000).default(""),
  receivedAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});

/** What the customer sees from their pay link. */
export interface PublicInvoice {
  /** The number the customer knows (an imported invoice's own number). */
  number: string;
  title: string;
  status: InvoiceStatus;
  company: { name: string; phone: string; email: string; logoUrl: string | null; primaryColor: string };
  customerName: string;
  address: string;
  lines: InvoiceLine[];
  totalCents: number;
  paidCents: number;
  balanceCents: number;
  dueDate: string | null;
  memo: string;
  /** Card payments allowed for this balance (up to the limit). */
  cardAllowed: boolean;
  cardLimitCents: number;
  surchargeBps: number;
  /** Stripe publishable key; null when online payment isn't set up. */
  stripeKey: string | null;
  pending: { method: PaymentMethodKind; amountCents: number; at: string }[];
}

/** Card: the customer entered a card (a Stripe PaymentMethod); the server prices it. */
export const cardQuoteSchema = z.object({ paymentMethodId: z.string().min(3).max(100) });
export const cardPaySchema = z.object({ paymentMethodId: z.string().min(3).max(100), surchargeCents: z.number().int().min(0), consent: z.literal(true) });
export const paymentConfirmedSchema = z.object({ paymentIntentId: z.string().regex(/^pi_[A-Za-z0-9]+$/) });
